const ANCHOR = /<a\b[^>]*?href=["']([^"']*)["'][^>]*>(.*?)<\/a>/gis;

export interface DescriptionSegment {
  text: string;
  /** Absent on a plain-text run. */
  href?: string;
}

const stripTags = (html: string) => html.replace(/<[^>]+>/g, '');

const decodeEntities = (text: string) =>
  text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    // Ampersand last, so an escaped entity in the source is not decoded twice.
    .replace(/&amp;/g, '&');

const clean = (html: string) => decodeEntities(stripTags(html)).replace(/\s+/g, ' ');

/**
 * Undefined for anything that is not http(s), which renders the run as plain text: this is
 * vendor HTML, so the scheme is gated the same way `markdownLinks` gates a citation's.
 */
const linkHref = (href: string) => (/^https?:\/\//i.test(href) ? href : undefined);

/**
 * Splits a Statista description into text runs and links. Anchors become real links because
 * they point at the sources behind the figure; every other tag Statista sends — paragraphs,
 * headings, `<b>` labels, `<br>` — is dropped, since the panel supplies its own typography.
 *
 * Returns a single plain segment when the description has no anchors, so callers need no
 * special case for the common shape.
 */
export const toDescriptionSegments = (html: string): DescriptionSegment[] => {
  const segments: DescriptionSegment[] = [];
  let cursor = 0;

  for (const match of html.matchAll(ANCHOR)) {
    const start = match.index;
    if (start > cursor) {
      const text = clean(html.slice(cursor, start));
      if (text) segments.push({ text });
    }

    const label = clean(match[2]);
    if (label) {
      const href = linkHref(match[1]);
      segments.push(href ? { text: label, href } : { text: label });
    }
    cursor = start + match[0].length;
  }

  const tail = clean(html.slice(cursor));
  if (tail) segments.push({ text: tail });

  return segments;
};
